/**
 * The three full-screening report tables (design §§9-11): Collections,
 * Written-Off/Charge-Off, Bankruptcy & Insolvency — thin wrappers over the
 * shared ReportTableCard anatomy, consuming the pure report-display
 * derivations. Each table renders only when its rows are non-empty (v1
 * parity; Written-Off renders from `predictions.writtenOffAccounts` and stays
 * absent when that key is empty/missing — data-map §8).
 * "Total Owing" chips are the recorded Σ-derivations (sumCollectionsOwing /
 * sumBankruptcyOwing — predictions carries no per-table total); Written-Off
 * has NO chip (verified in the export, spec §10). Column label "Original"
 * (not v1's "Original Amount") and the "&" heading are design-wins.
 */
import {
	type BankruptcyRowView,
	type CollectionRowView,
	deriveBankruptcyRows,
	deriveCollectionRows,
	deriveWriteOffRows,
	sumBankruptcyOwing,
	sumCollectionsOwing,
} from '#app/utils/application-detail-v2/report-display'
import { formatCurrency } from '#app/utils/misc'
import {
	ReportTableCard,
	type ReportTableRow,
	StatusPill,
	money,
} from './report-bits'

const COLLECTION_COLUMNS = [
	'Creditor',
	'Date',
	'Amount',
	'Original',
	'Status',
	'Remarks',
] as const

const BANKRUPTCY_COLUMNS = [
	'Reported',
	'Trustee',
	'Assets',
	'Liability',
	'Status',
	'Remarks',
] as const

const collectionRowCells = (rows: CollectionRowView[]): ReportTableRow[] =>
	rows.map((row, idx) => ({
		key: idx,
		cells: [
			row.creditor || '-',
			row.date || '-',
			money(row.amount),
			money(row.originalAmount),
			<StatusPill key="status" status={row.status} />,
			row.remarks || '-',
		],
	}))

const bankruptcyRowCells = (rows: BankruptcyRowView[]): ReportTableRow[] =>
	rows.map((row, idx) => ({
		key: idx,
		cells: [
			row.reported || '-',
			// Long trustee values truncate with an ellipsis (spec §11).
			<span
				key="trustee"
				title={row.trustee || undefined}
				className="block truncate"
			>
				{row.trustee || '-'}
			</span>,
			money(row.assets),
			money(row.liability),
			<StatusPill key="status" status={row.status} />,
			row.remarks || '-',
		],
	}))

export function CollectionsTable({
	aiReportData,
}: {
	aiReportData: Record<string, unknown> | null
}) {
	const rows = deriveCollectionRows(aiReportData)
	if (rows.length === 0) return null
	const total = sumCollectionsOwing(rows)
	return (
		<ReportTableCard
			title="Collections"
			iconSrc="/img/collections-icon.svg"
			owingTotal={total == null ? null : formatCurrency(total)}
			columnLabels={COLLECTION_COLUMNS}
			rows={collectionRowCells(rows)}
		/>
	)
}

export function WriteOffTable({
	aiReportData,
}: {
	aiReportData: Record<string, unknown> | null
}) {
	const rows = deriveWriteOffRows(aiReportData)
	if (rows.length === 0) return null
	return (
		<ReportTableCard
			title="Written-Off/Charge-Off"
			iconSrc="/img/written-off-icon.svg"
			columnLabels={COLLECTION_COLUMNS}
			rows={collectionRowCells(rows)}
		/>
	)
}

export function BankruptcyTable({
	aiReportData,
}: {
	aiReportData: Record<string, unknown> | null
}) {
	const rows = deriveBankruptcyRows(aiReportData)
	if (rows.length === 0) return null
	const total = sumBankruptcyOwing(rows)
	return (
		<ReportTableCard
			title="Bankruptcy & Insolvency"
			iconSrc="/img/bankcruptcy-icon.svg"
			owingTotal={total == null ? null : formatCurrency(total)}
			columnLabels={BANKRUPTCY_COLUMNS}
			rows={bankruptcyRowCells(rows)}
		/>
	)
}
